import React, { useState, useCallback, useEffect } from 'react';
import { isSameSecond } from 'date-fns';
import { get } from 'lodash';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import InputBase from '@mui/material/InputBase';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { FieldInput } from './field-inputs';
import { types } from './survey-types';

const inputStyle = {
  color: 'white',
  borderRadius: 3,
  overflow: 'hidden',
  backgroundColor: 'rgba(255,255,255,0.2)',
  paddingLeft: 8,
  paddingRight: 8,
};
const mapState = createSelector(
  ({ editSurvey }) => editSurvey,
  ({ surveyParcels }) => surveyParcels,
  ({ fieldDefinitions }) => fieldDefinitions,
  ({ reports }) => reports,
  (editSurvey, surveyParcels, fieldDefinitions, reports) => ({
    survey: editSurvey,
    surveyParcels,
    fieldDefinitions,
    reports,
  })
);

const prepareSurveyParcels = (surveyParcels, fieldDefinitions) => {
  const fields = Object.values(fieldDefinitions);
  return Object.values(surveyParcels).map((surveyParcel) => {
    const parcelSelections = {};

    fields.forEach((fieldDefinition) => {
      // eslint-disable-next-line no-param-reassign
      parcelSelections[fieldDefinition.id] = surveyParcel.field_changes
        .filter((fieldChange) => {
          return fieldChange.field_definition_id === fieldDefinition.id;
        })
        .sort((a, b) => a.created - b.created);
    });

    return {
      ...surveyParcel,
      parcelSelections,
    };
  });
};

const getSecondaryContent = (surveyParcel, fieldDefinition) => {
  const result = get(
    surveyParcel,
    `parcelSelections['${fieldDefinition.id}'][0].details`
  );
  return result;
};

const getPrimaryContent = (key, surveyParcel, fieldDefinition) => {
  switch (key) {
    case types.TEXT: {
      const result = get(
        surveyParcel,
        `parcelSelections['${fieldDefinition.id}'][0].text_value`,
        '–'
      );
      return result;
    }
    case types.CATEGORIES: {
      const date = get(
        surveyParcel,
        `parcelSelections['${fieldDefinition.id}'][0].created`
      );

      const categories = get(
        surveyParcel,
        `parcelSelections['${fieldDefinition.id}']`,
        []
      );
      return (
        categories
          .filter((cat) => isSameSecond(new Date(cat.created), new Date(date)))
          .map((cat) => cat.text_value)
          .join(', ') || '–'
      );
    }
    case types.YES_OR_NO:
    case types.NUMBER:
    case types.RANGE: {
      const result = get(
        surveyParcel,
        `parcelSelections['${fieldDefinition.id}'][0].number_value`,
        '–'
      );
      if (key === types.YES_OR_NO && result !== '–') {
        return result ? 'Yes' : 'No';
      }
      return result;
    }
    default:
      return '_';
  }
};

export const SurveyTracker = ({ open, handleClose = () => {} }) => {
  const {
    survey,
    surveyParcels: allSurveyParcels,
    fieldDefinitions: allFieldDefinitions,
    reports,
  } = useSelector(mapState);
  const dispatch = useDispatch();
  const fieldDefinitions = allFieldDefinitions[(survey || {}).id] || {};
  const [selectedField, setSelectedField] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedSurveyParcel, setSelectedSurveyParcel] = useState(null);
  const [values, setValues] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const surveyParcels = prepareSurveyParcels(
    allSurveyParcels[(survey || {}).id] || {},
    fieldDefinitions
  );

  useEffect(() => {
    if (survey == null) return;

    dispatch({
      type: 'FETCH_SURVEY',
      payload: { survey },
    });
  }, [survey, dispatch]);

  const finish = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const openParcelReport = useCallback(
    (parcelId) => {
      dispatch({
        type: 'OPEN_MODAL',
        payload: {
          type: 'PARCEL',
          data: {
            config: {
              parcel_id: parcelId,
              report: reports.find(({ id }) => id === 'PARCEL_REPORT'),
            },
          },
        },
      });
    },
    [dispatch]
  );

  const parcels = Object.values(surveyParcels).filter((parcel) => {
    if (!search) return true;
    if (parcel.address.toLowerCase().includes(search)) return true;
    if (parcel.parcel_id.toLowerCase().includes(search)) return true;
    return false;
  });

  return (
    <Dialog open={open} aria-labelledby="dialog-title" fullScreen>
      <TrackerHeader survey={survey} setSearch={setSearch} finish={finish} />

      <DialogContent style={{ height: '100%', marginTop: 64, padding: 0 }}>
        <TableContainer style={{ maxHeight: '100%' }}>
          <Table stickyHeader size="small" aria-label="Table of survey results">
            <TableHead style={{ minWidth: 100 }}>
              <TableRow>
                <TableCell>Parcel</TableCell>
                <TableCell>Address</TableCell>
                {Object.values(fieldDefinitions).map((fieldDefinition) => (
                  <TableCell key={fieldDefinition.id} align="center">
                    {fieldDefinition.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {parcels.map((surveyParcel) => (
                <TableRow key={surveyParcel.id}>
                  <TableCell component="th" scope="row">
                    <Button
                      onClick={() => openParcelReport(surveyParcel.parcel_id)}
                    >
                      {surveyParcel.parcel_id}
                    </Button>
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <Button
                      onClick={() => openParcelReport(surveyParcel.parcel_id)}
                    >
                      {surveyParcel.address}
                    </Button>
                  </TableCell>
                  {Object.values(fieldDefinitions).map((fieldDefinition) => (
                    <FieldTableCell
                      key={`${fieldDefinition.id}-${surveyParcel.id}`}
                      fieldDefinition={fieldDefinition}
                      surveyParcel={surveyParcel}
                      setSelectedField={setSelectedField}
                      setSelectedSurveyParcel={setSelectedSurveyParcel}
                      setValues={setValues}
                      setCategoryOptions={setCategoryOptions}
                      surveyParcels={surveyParcels}
                    />
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <FieldInput
        open={!!selectedField}
        survey={survey}
        selectedField={selectedField}
        categoryOptions={categoryOptions}
        selectedSurveyParcel={selectedSurveyParcel}
        setValues={setValues}
        handleClose={() => {
          setSelectedField(null);
          setValues([]);
        }}
        values={values}
      />
    </Dialog>
  );
};

const TrackerHeader = ({ survey, setSearch, finish }) => {
  const dispatch = useDispatch();
  const downloadCSV = () =>
    dispatch({ type: 'DOWNLOAD_SURVEY_CSV', payload: { survey } });
  return (
    <AppBar>
      <Toolbar style={{ justifyContent: 'space-between' }}>
        <Typography variant="h6">
          {survey == null ? 'Unnamed Survey' : survey.name}
        </Typography>

        <InputBase
          id="search"
          placeholder="Search"
          type="text"
          variant="outlined"
          onChange={(e) => setSearch(e.target.value.toLowerCase())}
          size="small"
          color="primary"
          style={inputStyle}
        />
        <div>
          <Button onClick={downloadCSV} color="inherit">
            Download CSV
          </Button>
          <Button onClick={finish} color="inherit">
            Close
          </Button>
        </div>
      </Toolbar>
    </AppBar>
  );
};

const FieldTableCell = ({
  fieldDefinition,
  surveyParcel,
  setSelectedField,
  setSelectedSurveyParcel,
  setValues,
  setCategoryOptions,
  surveyParcels,
}) => {
  const primaryContent = getPrimaryContent(
    fieldDefinition.type,
    surveyParcel,
    fieldDefinition
  );
  const secondaryContent = getSecondaryContent(surveyParcel, fieldDefinition);

  const openField = useCallback(() => {
    setSelectedField(fieldDefinition);
    setSelectedSurveyParcel(surveyParcel);
    setValues(surveyParcel.parcelSelections[fieldDefinition.id] || []);
    if (fieldDefinition.type === types.CATEGORIES) {
      setCategoryOptions(
        surveyParcels.reduce((acc, parcel) => {
          const rawValues = parcel.parcelSelections[fieldDefinition.id];
          return acc.concat(rawValues.map((v) => v.text_value)).filter(Boolean);
        }, [])
      );
    }
  }, [
    setSelectedField,
    setSelectedSurveyParcel,
    setValues,
    setCategoryOptions,
    surveyParcel,
    surveyParcels,
    fieldDefinition,
  ]);

  return (
    <TableCell align="center" style={{ maxWidth: 160 }}>
      <Button onClick={openField}>{primaryContent}</Button>
      {secondaryContent ? (
        <Typography variant="subtitle2">{secondaryContent}</Typography>
      ) : null}
    </TableCell>
  );
};
